import React, { createContext, useContext, useState, useCallback } from 'react';

const FULL_URL = process.env.REACT_APP_API_BASE;
const API_BASE = process.env.NODE_ENV === 'development'
  ? new URL(FULL_URL).pathname
  : FULL_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = sessionStorage.getItem('bi_user');
    return saved ? JSON.parse(saved) : null;
  });

  const [token, setToken] = useState(() => {
    return sessionStorage.getItem('bi_token') || null;
  });

  const login = useCallback(async (username, password) => {
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const contentType = res.headers.get('content-type') || '';
      let data = null;
      let rawText = '';

      if (contentType.includes('application/json')) {
        data = await res.json();
      } else {
        rawText = await res.text();
      }

      if (!res.ok) {
        if (rawText) {
          // eslint-disable-next-line no-console
          console.error('[auth] login failed', res.status, rawText);
        }
        const backendMessage = data && typeof data.message === 'string' ? data.message : '';
        let safeMessage;
        if (res.status >= 500) {
          safeMessage = 'The server is temporarily unavailable. Please try again in a moment.';
        } else if (res.status === 401 || res.status === 403) {
          safeMessage = backendMessage || 'Invalid username or password.';
        } else {
          safeMessage = backendMessage || 'We couldn\u2019t sign you in. Please try again.';
        }
        return { success: false, message: safeMessage };
      }

      if (!data) {
        return { success: false, message: 'Unexpected server response. Please try again.' };
      }

      const accessToken = data.accessToken;
      const u = data.user || {};
      const userData = {
        id: u.id,
        username: u.username || username,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || username,
        initials: ((u.firstName?.[0] || username[0] || 'A') + (u.lastName?.[0] || username[1] || 'D')).toUpperCase(),
        email: u.email,
        role: data.roles?.[0]?.code || 'ADMIN',
        projects: data.projects || [],
        clients: data.clients || [],
      };

      if (accessToken) {
        sessionStorage.setItem('bi_token', accessToken);
        setToken(accessToken);
      }

      sessionStorage.setItem('bi_user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[auth] login network error', err);
      return {
        success: false,
        message: 'Unable to reach the server. Check your internet connection and try again.',
      };
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('bi_user');
    sessionStorage.removeItem('bi_token');
    setUser(null);
    setToken(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function getToken() {
  return sessionStorage.getItem('bi_token');
}
